import { Control, Controller, FieldArrayWithId } from 'react-hook-form'

import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import { GetAdminVideoHostingQueryOutputProvider } from 'common/api/video-api-schema'

import s from '../VideoMainForm.module.css'

import { VideoMainFormValues } from '../videoMainForm.types'

type Props = {
  control: Control<VideoMainFormValues>
  fields: FieldArrayWithId<VideoMainFormValues, 'hostings', 'id'>[]
  onAdd: () => void
  onRemove: (index: number) => void
}

export const VideoHostingsSection = ({ control, fields, onAdd, onRemove }: Props) => {
  return (
    <Paper className={s.section} variant={'outlined'}>
      <div className={s.sectionHeader}>
        <Typography variant={'h6'}>Хостинги</Typography>
        <Button
          onClick={onAdd}
          size={'small'}
          startIcon={<AddIcon />}
          type={'button'}
          variant={'outlined'}
        >
          Добавить hosting
        </Button>
      </div>

      <Typography className={s.sectionHint} variant={'body2'}>
        Для каждой locale должен быть ровно один primary-хостинг. Secondary-хостинги допустимы
        только при наличии primary той же locale.
      </Typography>

      <div className={s.cards}>
        {fields.map((field, index) => (
          <div className={s.card} key={field.id}>
            <div className={s.cardHeader}>
              <Typography variant={'subtitle1'}>{`Hosting #${index + 1}`}</Typography>
              <IconButton
                disabled={fields.length === 1}
                onClick={() => onRemove(index)}
                size={'small'}
                type={'button'}
              >
                <DeleteOutlineIcon />
              </IconButton>
            </div>

            <Grid container spacing={2}>
              <Grid item md={3} xs={12}>
                <Controller
                  control={control}
                  name={`hostings.${index}.locale`}
                  render={({ field: localeField }) => (
                    <TextField
                      fullWidth
                      label={'Locale'}
                      placeholder={'ru'}
                      required
                      {...localeField}
                    />
                  )}
                />
              </Grid>

              <Grid item md={5} xs={12}>
                <Controller
                  control={control}
                  name={`hostings.${index}.provider`}
                  render={({ field: providerField }) => (
                    <FormControl fullWidth>
                      <InputLabel>Провайдер</InputLabel>
                      <Select label={'Провайдер'} {...providerField}>
                        <MenuItem value={GetAdminVideoHostingQueryOutputProvider.YOUTUBE}>
                          YouTube
                        </MenuItem>
                        <MenuItem value={GetAdminVideoHostingQueryOutputProvider.VIMEO}>
                          Vimeo
                        </MenuItem>
                        <MenuItem value={GetAdminVideoHostingQueryOutputProvider.VK}>VK</MenuItem>
                        <MenuItem value={GetAdminVideoHostingQueryOutputProvider.KINESCOPE}>
                          Kinescope
                        </MenuItem>
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item md={4} xs={12}>
                <Controller
                  control={control}
                  name={`hostings.${index}.isPrimary`}
                  render={({ field: primaryField }) => (
                    <FormControlLabel
                      className={s.primaryCheckbox}
                      control={
                        <Checkbox
                          checked={primaryField.value}
                          onChange={event => primaryField.onChange(event.target.checked)}
                        />
                      }
                      label={'Primary для locale'}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  control={control}
                  name={`hostings.${index}.sourceUrl`}
                  render={({ field: sourceUrlField }) => (
                    <TextField
                      fullWidth
                      helperText={
                        'Можно вставить ссылку: для YouTube и Vimeo hostingId/hostingHash будут извлечены при submit.'
                      }
                      label={'Ссылка на видео'}
                      {...sourceUrlField}
                    />
                  )}
                />
              </Grid>

              <Grid item md={4} xs={12}>
                <Controller
                  control={control}
                  name={`hostings.${index}.hostingId`}
                  render={({ field: hostingIdField }) => (
                    <TextField fullWidth label={'hostingId'} {...hostingIdField} />
                  )}
                />
              </Grid>

              <Grid item md={4} xs={12}>
                <Controller
                  control={control}
                  name={`hostings.${index}.hostingHash`}
                  render={({ field: hostingHashField }) => (
                    <TextField fullWidth label={'hostingHash'} {...hostingHashField} />
                  )}
                />
              </Grid>

              <Grid item md={4} xs={12}>
                <Controller
                  control={control}
                  name={`hostings.${index}.url`}
                  render={({ field: urlField }) => (
                    <TextField fullWidth label={'url'} {...urlField} />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  control={control}
                  name={`hostings.${index}.metadata`}
                  render={({ field: metadataField }) => (
                    <TextField
                      fullWidth
                      helperText={'Опционально. JSON-объект, например {"quality":"1080p"}'}
                      label={'metadata'}
                      minRows={3}
                      multiline
                      {...metadataField}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </div>
        ))}
      </div>
    </Paper>
  )
}
